import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import VStack from "../../ui_atoms/VStack";
import { useTheme } from "../../hooks/use-theme";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import Callout from "../../ui_atoms/Callout";
import {
    areVacationsManaged,
    formatCurrency,
    getContractPercentage,
    getContractWeeklyDurationInMinutes,
    getMissionPercentage,
    IjmSelection,
    getIsPayVacationIndemnityWithSalary,
    getIsPay13thSalaryWithSalary,
    getThirteenthSalarySummaryKeys,
    getSalaryTypeForRegime,
    isOnDemandWorkingTime,
    toFixedIfNecessary,
} from "../../utils";
import Loading from "../Loading";
import { useNavigation } from "@react-navigation/native";
import {
    Contract,
    EarningType,
    LppPlan,
    Mission,
    SalaryType,
    WorkerGroup,
} from "../../__generated__/graphql";
import { Buildable } from "ts-essentials";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { Salaries, useComputeSalary } from "./salary/hooks";
import SalaryBreakdown from "./salary/SalaryBreakdown";
import Gap from "../../ui_atoms/Gap";
import Switch from "../../ui_atoms/Switch";
import Text from "../Text";
import SelectField from "../SelectField";
import { useSetAtom } from "jotai";
import {
    currentClientMissionFeesRate,
    lastSalaryDetailsState,
} from "../../contexts/CreateMissionManager";
import { BodySmallText, TitleMediumText } from "../FormattedText";
import DropdownField from "../DropdownField";
import { OptionType } from "../OptionsSelector";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useBuildIjmDescription } from "../../hooks/ijm";
import ButtonField from "../ButtonField";
import { useIJMEditor } from "../../hooks/use-ijm-editor";
import { useLPPEditor } from "../../hooks/use-lpp-editor";
import { useVacationEditor } from "../../hooks/use-vacation-editor";
import { useThirteenthSalaryEditor } from "../../hooks/use-thirteenth-salary-editor";
import MoneyField from "../MoneyField";
import QuantityField from "../QuantityField";
import { canChangeIjm } from "../../utils/ijm-utils";
import { useContractCct } from "../../hooks/cct-utils";
import { useLoadContractLppPlans } from "../../hooks/lpp-utils";
import { canChangeLppConfiguration, getLppPlanFromConfiguration } from "../../utils/lpp-utils";
import { buildDescription } from "../../ui_atoms/ListItem";
import MarkdownView from "../MarkdownView";

const getSalaryInfo = (salaryType: SalaryType, salaryDetails?: Salaries | null) => {
    if (!salaryDetails) {
        return {
            salary: 0,
            minSalary: 0,
        };
    }
    switch (salaryType) {
        case SalaryType.Base:
            return {
                salary: salaryDetails.baseSalary,
                minSalary: salaryDetails.minBaseSalary,
            };
        case SalaryType.Gross:
            return {
                salary: salaryDetails.grossSalary,
                minSalary: salaryDetails.minGrossSalary,
            };
        case SalaryType.Net:
            return {
                salary: salaryDetails.netSalary,
                minSalary: salaryDetails.minNetSalary,
            };
        case SalaryType.Invoice:
            return {
                salary: salaryDetails.invoiceSalary,
                minSalary: salaryDetails.minInvoiceSalary,
            };
        default:
            return {
                salary: 0,
                minSalary: 0,
            };
    }
};

export const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                salaryStyle: {
                    flexShrink: 1,
                    flexGrow: 1,
                    flexBasis: "auto",
                },
                salaryInputStyle: {
                    ...fonts.headlineMedium,
                    textAlign: "right",
                },
                markdownLink: {
                    color: colors.secondary,
                },
                helpButtonIcon: {
                    margin: 0,
                    marginBottom: -21,
                    position: "relative",
                    right: 2,
                    top: -10,
                },
            }),
        [colors, fonts],
    );
};

type MissionSalaryProps = {
    mission: Buildable<Mission>;
    ijmSelection?: IjmSelection | null;
    initialSalary?: number;
    setSalaries: (salaries: Salaries) => void;
    setVacationdays: (vacationDays: number) => void;
    setHas13thSalary: (has13thSalary: boolean) => void;
    setPay13thSalaryWithSalary: (value: boolean) => void;
    setTotalHours: (hours: number) => void;
    setIjmSelection: (selection: IjmSelection) => void;
    setLppPlan: (lppPlan: LppPlan | null) => void;
    setWorkerGroup: (workerGroup: WorkerGroup | null) => void;
    setForceAvs: (value: boolean) => void;
    setForceLpp: (value: boolean) => void;
    setPayVacationIndemnityWithSalary: (value: boolean) => void;
    setLoading?: (loading: boolean) => void;
};

type ScreenParams = StackScreenProps<RootStackParamList>;

const getMaxHours = (startDate: Date | null, endDate: Date | null) => {
    const dayMaxHours = 11.5;

    if (!startDate || !endDate) {
        return 28 * dayMaxHours;
    }
    // +1 to include the last day as when we work for just 1 day, start and end date are the same
    const nbDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const nbWeeks = Math.floor(nbDays / 7);
    const remainingDays = nbDays % 7;
    // max 6 days per week
    return nbWeeks * (dayMaxHours * 6) + remainingDays * dayMaxHours;
};

const MissionSalary: React.FC<MissionSalaryProps> = ({
    mission,
    ijmSelection,
    setSalaries,
    initialSalary,
    setHas13thSalary,
    setPay13thSalaryWithSalary,
    setVacationdays,
    setTotalHours,
    setIjmSelection,
    setLppPlan,
    setWorkerGroup,
    setForceAvs,
    setForceLpp,
    setPayVacationIndemnityWithSalary,
    setLoading,
}) => {
    const { fonts, gridSize, colors } = useTheme();
    const i18n = useI18n();
    const styles = useStyles();
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const [salaryInternalValue, setSalaryInternalValue] = useState<number | undefined>(
        initialSalary ? initialSalary / 100 : undefined,
    );
    const [hoursInternalValue, setHoursInternalValue] = useState(
        mission.currentContract?.workingTimeClassicSpec?.unitCount || undefined,
    );
    const [salaryValue, setSalaryValue] = useState<number>();
    const [isSalaryTooLow, setIsSalaryTooLow] = useState(false);
    // On a 13x contract the monthly amount paid is the base salary, so it replaces the gross.
    const is13x = !getIsPay13thSalaryWithSalary({
        earningType: mission.currentContract?.earningType,
        has13thSalary: mission.currentContract?.has13thSalary,
        pay13thSalaryWithSalary: mission.currentContract?.pay13thSalaryWithSalary,
    });
    const [salaryType, setSalaryType] = useState<SalaryType>(
        getSalaryTypeForRegime(SalaryType.Gross, is13x),
    );
    useEffect(() => {
        // Keeps the typed amount and reinterprets it, as any salary type change does.
        setSalaryType((current) => getSalaryTypeForRegime(current, is13x));
    }, [is13x]);

    const contractPercentage = useMemo(
        () => getContractPercentage(mission.currentContract as Contract) || 0,
        [mission.currentContract],
    );
    const setCurrentClientMissionFeesRate = useSetAtom(currentClientMissionFeesRate);
    const setLastSalaryDetails = useSetAtom(lastSalaryDetailsState);
    const startDate = useMemo(
        () =>
            mission.currentContract?.startDate ? new Date(mission.currentContract.startDate) : null,
        [mission.currentContract?.startDate],
    );
    const endDate = useMemo(
        () => (mission.currentContract?.endDate ? new Date(mission.currentContract.endDate) : null),
        [mission.currentContract?.endDate],
    );

    const handleSetSalaries = React.useCallback(
        (salaries: Salaries | null) => {
            let newSalaryTooLow;
            if (!salaries) {
                // no computed salary means no minimum to compare to: keep the error away instead
                // of leaving the previous one on screen with a stale minimum
                setIsSalaryTooLow(false);
                setSalaries({
                    requestedSalaryType: salaryType,
                    invoiceSalary: 0,
                    netSalary: 0,
                    deferredIndemnity: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
                return;
            }
            const { salary, minSalary } = getSalaryInfo(salaryType, salaries);
            //everytime we receive a computed salary we update the field in case salary has been updated
            if (mission.currentContract?.earningType === EarningType.Monthly) {
                if (contractPercentage) {
                    newSalaryTooLow = (minSalary * contractPercentage) / 100 > salary;
                } else {
                    newSalaryTooLow = false;
                }
            } else {
                newSalaryTooLow = minSalary > salary;
            }

            setIsSalaryTooLow(newSalaryTooLow);
            if (!newSalaryTooLow) {
                setSalaries(salaries);
            } else {
                setSalaries({
                    requestedSalaryType: SalaryType.Gross,
                    invoiceSalary: 0,
                    netSalary: 0,
                    deferredIndemnity: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
            }
            let adjustedSalaryValue = 0;
            switch (salaries.requestedSalaryType) {
                case SalaryType.Gross:
                    adjustedSalaryValue = salaries.grossSalary;
                    break;
                case SalaryType.Net:
                    adjustedSalaryValue = salaries.netSalary;
                    break;
                case SalaryType.Invoice:
                    adjustedSalaryValue = salaries.invoiceSalary;
                    break;
                case SalaryType.Base:
                    adjustedSalaryValue = salaries.baseSalary;
                    break;
            }
            // Minimum 0.02 CHF
            if (adjustedSalaryValue < 2) {
                adjustedSalaryValue = 2;
            }
            setSalaryInternalValue(adjustedSalaryValue / 100);
        },
        [setSalaries, mission.currentContract?.earningType, contractPercentage, salaryType],
    );

    const currentProfile = useCurrentProfile();
    const currentCct = useContractCct(mission.currentContract as Contract);
    const availableLppPlans = useLoadContractLppPlans(currentCct?.cctContractId);

    // Worker Group state and logic
    const [selectedWorkerGroupId, setSelectedWorkerGroupId] = useState<string | undefined>(
        mission.workerGroup?.id,
    );

    const availableWorkerGroups = useMemo(
        () => currentProfile?.client?.workerGroups || [],
        [currentProfile?.client?.workerGroups],
    );
    const hasWorkerGroups = useMemo(
        () => availableWorkerGroups.length > 0,
        [availableWorkerGroups],
    );

    const canChangeLppPlan = useMemo(() => {
        if (!currentProfile?.client || !currentCct || !availableLppPlans) {
            return false;
        }
        return canChangeLppConfiguration(
            currentProfile?.client,
            currentCct,
            availableLppPlans,
            "all",
        );
    }, [currentProfile?.client, currentCct, availableLppPlans]);

    // Hide the LPP row while plans are loading and when there is a single available plan:
    // a single plan is selected by default and cannot be changed, so there is nothing for
    // the user to do here.
    const showLpp = useMemo(
        () => availableLppPlans !== undefined && availableLppPlans.length !== 1,
        [availableLppPlans],
    );

    const contractLppPlan = useMemo(() => {
        if (!availableLppPlans) {
            return undefined;
        }
        // in case contract has a selected plan and plan is available, we use it
        if (mission?.currentContract?.lppPlan !== undefined) {
            if (
                // a null plan ("no LPP") is only trustworthy when the current CCT indeed has
                // no plans, otherwise it is stale (e.g. CCT changed) and must be re-evaluated
                (mission?.currentContract?.lppPlan === null && availableLppPlans.length === 0) ||
                availableLppPlans.find((plan) => plan.id === mission.currentContract?.lppPlan?.id)
            ) {
                return mission?.currentContract?.lppPlan as LppPlan;
            }
        }
        // if not we try to see if we can get it from lpp configuration
        if (currentProfile?.client && currentCct) {
            const configuredPlan = getLppPlanFromConfiguration(
                currentProfile?.client,
                currentCct,
                "all",
                availableLppPlans,
            );
            // the worker-group flow has no LPP selection row, so we keep the historical
            // default (first available plan) instead of requiring an impossible selection
            if (configuredPlan === undefined && hasWorkerGroups) {
                return availableLppPlans[0] ?? null;
            }
            return configuredPlan;
        }

        return null;
    }, [
        currentProfile?.client,
        currentCct,
        mission?.currentContract?.lppPlan,
        availableLppPlans,
        hasWorkerGroups,
    ]);

    const { computeSalary, salaryDetails, loading, error } = useComputeSalary(
        mission as Mission,
        ijmSelection?.ijmGroupId,
        contractLppPlan,
        handleSetSalaries,
        setVacationdays,
        setHas13thSalary,
        setLppPlan,
        setPay13thSalaryWithSalary,
    );

    useEffect(() => {
        // used to disabled buttons while loading in upper component.
        setLoading?.(loading);
    }, [setLoading, loading]);

    React.useEffect(() => {
        setCurrentClientMissionFeesRate(
            salaryDetails?.getSalaryDetails.clientMissionFeesRate || "0",
        );
        setLastSalaryDetails(salaryDetails);
    }, [salaryDetails, setCurrentClientMissionFeesRate, setLastSalaryDetails]);

    const contractEarningType = useMemo(
        () => mission.currentContract?.earningType || EarningType.Monthly,
        [mission.currentContract?.earningType],
    );

    // Force-AVS: opt a salary back into AVS when it would otherwise be exempt (small-salary
    // exemption). The computed salary has no explicit exemption flag, so we infer it from the
    // absence of the worker-side AVS deduction line (stable code "avs"). Once the toggle is ON,
    // the recomputed salary contains an AVS line again, so we keep the row visible while forced.
    // On demand nothing can be projected: the toggle is the AVS decision itself, always shown.
    const isOnDemand = isOnDemandWorkingTime(mission.currentContract?.workingTimeClassicSpec);
    const forceAvs = !!mission.currentContract?.disableAvsSmallSalaryExemption;
    const forceLpp = !!mission.currentContract?.forceLpp;
    const showForceAvs = useMemo(() => {
        if (isOnDemand) {
            return true;
        }
        if (!salaryDetails) {
            return false;
        }
        const hasAvsDeduction = (salaryDetails.getSalaryDetails.workerDeductions ?? []).some(
            (deduction) => deduction.title === "avs" && deduction.amount > 0,
        );
        return forceAvs || !hasAvsDeduction;
    }, [isOnDemand, salaryDetails, forceAvs]);

    const toggleForceAvs = useCallback(() => {
        if (isOnDemand && forceAvs) {
            // LPP requires AVS: exempting an on-demand salary from AVS drops LPP too
            setForceLpp(false);
        }
        setForceAvs(!forceAvs);
    }, [isOnDemand, forceAvs, setForceAvs, setForceLpp]);

    const renderForceAvs = () =>
        showForceAvs && (
            <ButtonField
                style={{ marginHorizontal: -gridSize, paddingHorizontal: gridSize }}
                asListAction="primary"
                text={i18n.t("screens.user.create_mission_salary.forceAvsAction")}
                right={
                    <View pointerEvents="none">
                        <Switch value={forceAvs} size={44} />
                    </View>
                }
                onPress={toggleForceAvs}
                info={
                    <BodySmallText color={colors.onSurfaceDisabled}>
                        {i18n.t(
                            isOnDemand
                                ? "screens.user.create_mission_salary.onDemandAvsLegend"
                                : "screens.user.create_mission_salary.forceAvsLegend",
                        )}
                    </BodySmallText>
                }
            />
        );

    // On demand the LPP entry threshold cannot be judged: whether LPP applies is chosen here.
    // Offered only when a plan is selected, and locked while the salary is exempted from AVS.
    const renderOnDemandLpp = () =>
        isOnDemand &&
        !!contractLppPlan && (
            <ButtonField
                style={{ marginHorizontal: -gridSize, paddingHorizontal: gridSize }}
                asListAction="primary"
                text={i18n.t("screens.user.create_mission_salary.onDemandLppAction")}
                disabled={!forceAvs}
                right={
                    <View pointerEvents="none">
                        <Switch value={forceLpp} disabled={!forceAvs} size={44} />
                    </View>
                }
                onPress={() => setForceLpp(!forceLpp)}
                info={
                    <BodySmallText color={colors.onSurfaceDisabled}>
                        {i18n.t("screens.user.create_mission_salary.onDemandLppLegend")}
                    </BodySmallText>
                }
            />
        );

    React.useEffect(
        () => {
            let noInfo = salaryInternalValue === undefined;
            if (
                contractEarningType === EarningType.Package &&
                (mission.currentContract?.workingTimeClassicSpec?.unitCount || 0) <= 0
            ) {
                noInfo = true;
            }
            if (noInfo) {
                void computeSalary(0, salaryType);
            } else {
                void computeSalary(salaryInternalValue as number, salaryType);
            }
            // in this case we want to react only on salaryValue change so that the
            // salaryInternalValue is only used as data source of the TextInput
        },
        [salaryValue, salaryType, contractEarningType, computeSalary], //eslint-disable-line react-hooks/exhaustive-deps
    );

    const workerGroupItems = useMemo(() => {
        return availableWorkerGroups.map((wg) => ({
            label: wg.name,
            value: wg.id,
            description: wg.description || undefined,
        }));
    }, [availableWorkerGroups]);

    const onTextFieldChange = useCallback(
        (text?: number) => {
            if (salaryInternalValue !== text) {
                // reset salary values to disable the "next" button
                setSalaries({
                    requestedSalaryType: salaryType,
                    invoiceSalary: 0,
                    netSalary: 0,
                    deferredIndemnity: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
            }
            setSalaryInternalValue(text);
        },
        [salaryInternalValue, salaryType, setSalaries],
    );

    const onHoursFieldChange = useCallback(
        (newValue = 0) => {
            if (hoursInternalValue !== newValue) {
                setSalaries({
                    requestedSalaryType: salaryType,
                    invoiceSalary: 0,
                    netSalary: 0,
                    deferredIndemnity: 0,
                    grossSalary: 0,
                    baseSalary: 0,
                    minBaseSalary: 0,
                    minGrossSalary: 0,
                    minNetSalary: 0,
                    minInvoiceSalary: 0,
                });
            }
            setHoursInternalValue(newValue);
        },
        [hoursInternalValue, salaryType, setSalaries],
    );

    const formatDuration = useDurationFormatter();
    const workingWeeklyDurationInMinutes = useMemo(() => {
        return getContractWeeklyDurationInMinutes(mission.currentContract as Contract);
    }, [mission]);

    const salaryTypeData = useMemo(
        () =>
            [
                is13x
                    ? {
                          label: i18n.t("screens.user.create_mission_salary.salaryType.base_13x"),
                          value: SalaryType.Base,
                      }
                    : {
                          label: i18n.t("screens.user.create_mission_salary.salaryType.gross"),
                          value: SalaryType.Gross,
                      },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.net"),
                    value: SalaryType.Net,
                },
                {
                    label: i18n.t("screens.user.create_mission_salary.salaryType.invoice"),
                    value: SalaryType.Invoice,
                },
            ] as [OptionType<SalaryType>, OptionType<SalaryType>, OptionType<SalaryType>],
        [i18n, is13x],
    );

    const { minSalary } = useMemo(() => {
        return getSalaryInfo(salaryType, salaryDetails?.getSalaryDetails);
    }, [salaryDetails?.getSalaryDetails, salaryType]);

    const minSalaryError = useMemo(() => {
        if (loading || error || !isSalaryTooLow) {
            return undefined;
        }
        // every earning type has its own wording, each consuming a subset of these variables
        return i18n.t(
            `screens.user.create_mission_salary_${contractEarningType}.min_salary_info_plus`,
            {
                percentage: contractPercentage,
                percentageAmount: formatCurrency({
                    amountInCents: (contractPercentage / 100) * minSalary,
                    displayDigitsWhenNoCents: false,
                }),
                amount: formatCurrency({
                    amountInCents: minSalary,
                    displayDigitsWhenNoCents: false,
                }),
                hours: hoursInternalValue ?? 0,
                salaryType: i18n
                    .t(`screens.user.create_mission_salary.salaryType.${salaryType}`)
                    .toLowerCase(),
            },
        );
    }, [
        contractEarningType,
        contractPercentage,
        error,
        hoursInternalValue,
        i18n,
        isSalaryTooLow,
        loading,
        minSalary,
        salaryType,
    ]);

    const maxHours = useMemo(() => {
        return getMaxHours(startDate, endDate);
    }, [endDate, startDate]);

    const onChangeHours = useCallback(() => {
        if (hoursInternalValue) {
            if (hoursInternalValue > maxHours) {
                setHoursInternalValue(maxHours);
                setTotalHours(hoursInternalValue);
            }
            setTotalHours(hoursInternalValue);
        }
    }, [hoursInternalValue, setTotalHours, maxHours]);

    const verifyMaxHours = useCallback(
        (start: Date | null, end: Date | null) => {
            const nbHours = getMaxHours(start, end);
            setHoursInternalValue((old) => {
                if (old && old > nbHours) {
                    setTotalHours(nbHours);
                    return nbHours;
                }
                return old;
            });
        },
        [setTotalHours],
    );

    useEffect(() => {
        if (contractEarningType === EarningType.Package) {
            verifyMaxHours(startDate, endDate);
        }
    }, [startDate, endDate, verifyMaxHours, contractEarningType]);

    useEffect(() => {
        // this effect fix the IJM configuration is mission is not well setup
        if (
            (!ijmSelection || ijmSelection.ijmGroupId === undefined) &&
            mission?.currentContract?.ijmInfo
        ) {
            setIjmSelection({
                ijmGroupId: mission?.currentContract?.ijmInfo.ijmGroup?.id || null,
                ijmGroupName: mission?.currentContract?.ijmInfo.ijmGroup?.name,
                ijmPayRemainingPercents: mission?.currentContract?.ijmInfo.payRemainingPercents,
                ijmPayFirstDays: mission?.currentContract?.ijmInfo.payFirstDays,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Update parent when worker group selection changes
        if (selectedWorkerGroupId) {
            const selectedGroup = availableWorkerGroups.find(
                (wg) => wg.id === selectedWorkerGroupId,
            );
            setWorkerGroup(selectedGroup || null);
        }
    }, [selectedWorkerGroupId, availableWorkerGroups, setWorkerGroup]);

    const salaryMaxValue = useMemo(() => {
        switch (contractEarningType) {
            case EarningType.Monthly:
                return 50_000;
            case EarningType.Daily:
                return 10_000;
            case EarningType.Hourly:
                return 5_000;
            case EarningType.Package:
                return 1_000_000;
            default:
                return 0;
        }
    }, [contractEarningType]);

    const isDisplayVacationPercentage = useMemo(() => {
        return !!(contractPercentage && contractPercentage !== 100);
    }, [contractPercentage]);

    // Vacation indemnity can be paid directly or kept in a "cagnotte" (accrual) only when
    // vacations are managed: hourly/daily recurring contracts that are not short missions
    // (not oneshot, not package, not a fixed-term contract shorter than 3 months). Cagnotting
    // is mandatory (forced ON) when the activity rate is 100% or when the legal employer uses LSE.
    const showCagnotteVacations = useMemo(
        () => areVacationsManaged(mission.currentContract),
        [mission.currentContract],
    );
    const isCagnotteMandatory = useMemo(
        () => contractPercentage === 100 || !!mission.legalEmployer?.useLseCct,
        [contractPercentage, mission.legalEmployer?.useLseCct],
    );
    const payVacationIndemnityWithSalary =
        !!mission.currentContract?.payVacationIndemnityWithSalary;

    useEffect(() => {
        if (!showCagnotteVacations) {
            // When vacations are not managed (short missions), the indemnity is always paid
            // directly and the user cannot choose otherwise.
            if (!payVacationIndemnityWithSalary) {
                setPayVacationIndemnityWithSalary(true);
            }
        } else if (isCagnotteMandatory && payVacationIndemnityWithSalary) {
            // When cagnotting is mandatory, keep the persisted value in sync so the indemnity is
            // not paid directly (kept in the cagnotte).
            setPayVacationIndemnityWithSalary(false);
        }
    }, [
        showCagnotteVacations,
        isCagnotteMandatory,
        payVacationIndemnityWithSalary,
        setPayVacationIndemnityWithSalary,
    ]);

    const vacationDayLabel = useMemo(() => {
        const days = mission.currentContract?.vacationDays;
        if (!days) {
            return undefined;
        }
        const fullWeek = days % 5 === 0;
        const nbWeeks = Math.floor(days / 5);
        return i18n.t(
            `screens.user.create_mission_salary.vacation_days${fullWeek ? "_full_week" : ""}_format`,
            { days, nbWeeks },
        );
    }, [i18n, mission.currentContract?.vacationDays]);

    const vacationSummary = useMemo(() => {
        const status = !showCagnotteVacations
            ? undefined
            : isCagnotteMandatory
              ? i18n.t("screens.user.create_mission_salary.cagnotageObligatoire")
              : payVacationIndemnityWithSalary
                ? i18n.t("screens.user.create_mission_salary.cagnotageNonApplique")
                : i18n.t("screens.user.create_mission_salary.cagnotageApplique");
        return [vacationDayLabel, status].filter(Boolean).join(" • ");
    }, [
        showCagnotteVacations,
        isCagnotteMandatory,
        payVacationIndemnityWithSalary,
        vacationDayLabel,
        i18n,
    ]);

    const canChangeIjmGroup = useMemo(() => {
        if (!currentProfile?.client || !currentCct) {
            return false;
        }
        return canChangeIjm(currentProfile?.client, currentCct, "all");
    }, [currentProfile?.client, currentCct]);
    const buildIjmDescription = useBuildIjmDescription(" • ");

    const editIjm = useIJMEditor();
    const editLpp = useLPPEditor();
    const editVacation = useVacationEditor();
    const doEditIjm = useCallback(() => {
        editIjm(
            {
                currentSelection: ijmSelection,
                cct: currentCct,
                useLse: mission.clientInfo?.useLse,
                salaryInfo:
                    mission.currentContract?.grossSalary && mission.currentContract?.earningType
                        ? {
                              amount: mission.currentContract.grossSalary,
                              earningType: mission.currentContract.earningType,
                          }
                        : undefined,
            },
            (selection) => {
                if (selection) {
                    setIjmSelection(selection);
                }
            },
        );
    }, [
        editIjm,
        currentCct,
        ijmSelection,
        mission.currentContract?.earningType,
        mission.currentContract?.grossSalary,
        setIjmSelection,
        mission.clientInfo?.useLse,
    ]);

    const doEditLppPlan = useCallback(() => {
        if (!availableLppPlans) {
            return;
        }
        editLpp(
            {
                currentPlan: contractLppPlan,
                availablePlans: availableLppPlans,
                useLse: !!mission.clientInfo?.useLse,
                cct: currentCct,
            },
            (plan) => {
                if (plan !== undefined) {
                    setLppPlan(plan);
                }
            },
        );
    }, [
        editLpp,
        contractLppPlan,
        availableLppPlans,
        currentCct,
        setLppPlan,
        mission.clientInfo?.useLse,
    ]);

    const doEditVacation = useCallback(() => {
        editVacation(
            {
                vacationDays: mission.currentContract?.vacationDays ?? undefined,
                payVacationIndemnityWithSalary,
                isCagnotteMandatory,
                isVacationTracked: showCagnotteVacations,
                minVacationDays: salaryDetails?.getSalaryDetails.minVacationDays || 20,
                contractPercentage,
                earningType: contractEarningType,
            },
            ({ vacationDays, payVacationIndemnityWithSalary: newPayWithSalary }) => {
                if (vacationDays) {
                    setVacationdays(vacationDays);
                }
                setPayVacationIndemnityWithSalary(newPayWithSalary);
            },
        );
    }, [
        editVacation,
        mission.currentContract?.vacationDays,
        payVacationIndemnityWithSalary,
        isCagnotteMandatory,
        showCagnotteVacations,
        salaryDetails?.getSalaryDetails.minVacationDays,
        contractPercentage,
        contractEarningType,
        setVacationdays,
        setPayVacationIndemnityWithSalary,
    ]);

    const isThirteenthSalaryMandatory = !!salaryDetails?.getSalaryDetails.mandatory13thMonth;
    const thirteenthSalarySummary = useMemo(
        () =>
            getThirteenthSalarySummaryKeys(mission.currentContract, isThirteenthSalaryMandatory)
                .map((key) => i18n.t(key))
                .join(" • "),
        [mission.currentContract, isThirteenthSalaryMandatory, i18n],
    );
    const editThirteenthSalary = useThirteenthSalaryEditor();
    const doEditThirteenthSalary = useCallback(() => {
        editThirteenthSalary(
            {
                has13thSalary: !!mission.currentContract?.has13thSalary,
                pay13thSalaryWithSalary: getIsPay13thSalaryWithSalary(mission.currentContract),
                isMandatory: isThirteenthSalaryMandatory,
                earningType: contractEarningType,
            },
            (result) => {
                setHas13thSalary(result.has13thSalary);
                setPay13thSalaryWithSalary(result.pay13thSalaryWithSalary);
            },
        );
    }, [
        editThirteenthSalary,
        mission.currentContract,
        isThirteenthSalaryMandatory,
        contractEarningType,
        setHas13thSalary,
        setPay13thSalaryWithSalary,
    ]);

    const onSalaryBlur = useCallback(() => {
        if (salaryInternalValue !== salaryValue) {
            setSalaryValue(salaryInternalValue);
        }
    }, [salaryInternalValue, salaryValue]);

    const getIjmAndLppPlanInfo = useCallback(() => {
        const ijmDescription = buildIjmDescription(
            ijmSelection?.ijmGroupName || undefined,
            mission.clientInfo?.useLse ? ijmSelection?.ijmPayFirstDays : null,
            ijmSelection?.ijmPayRemainingPercents,
        );
        const infoMarkdown = [
            i18n.t("screens.user.create_mission_salary.ijmInfoTitle"),
            ijmDescription,
            i18n.t("screens.user.create_mission_salary.lppInfoTitle"),
            contractLppPlan?.name || i18n.t("screens.user.create_mission_salary.no_lpp_plan"),
        ];
        return buildDescription(infoMarkdown, { separator: "\n" }) || "";
    }, [ijmSelection, contractLppPlan, mission.clientInfo?.useLse, i18n, buildIjmDescription]);
    return (
        <>
            <VStack>
                <VStack>
                    {contractEarningType !== EarningType.Package && <Gap gap={1 / 2} />}
                    {contractEarningType === EarningType.Package && (
                        <VStack>
                            <Gap gap={1 / 2} />
                            <BodySmallText>
                                {i18n.t(
                                    "screens.user.create_mission_salary_package.requested_salary_title",
                                )}
                            </BodySmallText>
                            <Gap gap={1 / 2} />
                        </VStack>
                    )}
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <MoneyField
                            selectTextOnFocus
                            disabled={loading}
                            style={styles.salaryStyle}
                            minValue={0.02}
                            maxValue={salaryMaxValue}
                            inputStyle={styles.salaryInputStyle}
                            label={i18n.t(
                                `screens.user.create_mission_salary_${contractEarningType}.requested_salary_label`,
                            )}
                            value={salaryInternalValue}
                            onBlur={onSalaryBlur}
                            onChange={onTextFieldChange}
                            leftColor={colors.greyAccessible}
                            labelLeftPadding={8}
                            withIosDoneButton
                            // the field error text is full width, it would be laid out as a third
                            // item of this row: the message is rendered below the row instead
                            error={!!minSalaryError}
                            showErrorText={false}
                        />
                        <Gap gap={1 / 2} />
                        <DropdownField<SalaryType>
                            numberOfLines={1}
                            style={{ flexShrink: 1, flexGrow: 1, flexBasis: "75%" }}
                            items={salaryTypeData}
                            onSelect={setSalaryType}
                            value={salaryType}
                        />
                    </View>
                    {minSalaryError && (
                        <BodySmallText color={colors.error}>{minSalaryError}</BodySmallText>
                    )}
                    <Gap gap={1} />
                    {contractEarningType === EarningType.Monthly &&
                        workingWeeklyDurationInMinutes && (
                            <BodySmallText color={colors.onSurfaceDisabled}>
                                {i18n.t(
                                    `screens.user.create_mission_salary_monthly.working_percentage_info`,
                                    {
                                        percentage: getMissionPercentage(mission as Mission),
                                        duration: formatDuration(workingWeeklyDurationInMinutes),
                                    },
                                )}
                            </BodySmallText>
                        )}
                    {contractEarningType === EarningType.Package && (
                        <>
                            <Gap gap={1 / 2} />
                            <BodySmallText>
                                {i18n.t(
                                    "screens.user.create_mission_salary_package.requested_hours_title",
                                )}
                            </BodySmallText>
                            <Gap gap={1 / 2} />
                            <QuantityField
                                selectTextOnFocus
                                disabled={loading}
                                maxValue={maxHours}
                                value={hoursInternalValue}
                                decimalPlaces={2}
                                onBlur={onChangeHours}
                                onChange={onHoursFieldChange}
                                keyboardType="decimal-pad"
                                returnKeyType="done"
                                inputStyle={{ textAlign: "right" }}
                            />
                            <BodySmallText color={colors.onSurfaceDisabled}>
                                {i18n.t("screens.user.create_mission_salary_package.max_hours", {
                                    hours: maxHours,
                                })}
                            </BodySmallText>
                        </>
                    )}
                    {/* Vacations INFO */}
                    <Gap gap={1} />
                    <TitleMediumText>
                        {i18n.t(
                            showCagnotteVacations
                                ? "screens.user.create_mission_salary.vacationAndCagnotageTitle"
                                : "screens.user.create_mission_salary.vacationExplained",
                        )}
                    </TitleMediumText>
                    <ButtonField
                        style={{
                            marginHorizontal: -gridSize,
                            paddingHorizontal: gridSize,
                        }}
                        asListAction={
                            mission.currentContract?.vacationDays ? "primary" : "secondary"
                        }
                        text={
                            vacationSummary ||
                            i18n.t("screens.user.create_mission_salary.setVacationDays")
                        }
                        right="gear"
                        onPress={doEditVacation}
                        info={
                            isDisplayVacationPercentage && (
                                <BodySmallText color={colors.onSurfaceDisabled}>
                                    {i18n.t(
                                        "screens.user.create_mission_salary.vacation_percentage",
                                        {
                                            percent: contractPercentage,
                                            count: toFixedIfNecessary(
                                                ((mission.currentContract?.vacationDays || 0) *
                                                    contractPercentage) /
                                                    100,
                                                2,
                                            ),
                                        },
                                    )}
                                </BodySmallText>
                            )
                        }
                    />
                    <Gap gap={1.5} />

                    <TitleMediumText>
                        {i18n.t("screens.user.create_mission_salary.thirteenth_salary_title")}
                    </TitleMediumText>
                    <ButtonField
                        testID="thirteenth-salary-row"
                        style={{
                            marginHorizontal: -gridSize,
                            paddingHorizontal: gridSize,
                        }}
                        asListAction="primary"
                        text={thirteenthSalarySummary}
                        right="gear"
                        onPress={doEditThirteenthSalary}
                    />
                    <Gap gap={1.5} />

                    {/* Worker Group, IJM, and LPP INFO */}
                    {hasWorkerGroups ? (
                        <>
                            <TitleMediumText>
                                {i18n.t("screens.user.create_mission_salary.workerGroupExplained")}
                            </TitleMediumText>
                            <SelectField
                                style={{
                                    marginHorizontal: -gridSize,
                                    paddingHorizontal: gridSize,
                                }}
                                asListAction={selectedWorkerGroupId ? "primary" : "secondary"}
                                onSelect={({ value }) => {
                                    if (value) {
                                        setSelectedWorkerGroupId(value);
                                    }
                                }}
                                data={workerGroupItems}
                                placeholder={i18n.t(
                                    "screens.user.create_mission_salary.setWorkerGroup",
                                )}
                                value={selectedWorkerGroupId}
                                right="gear"
                                labelLeftPadding={gridSize}
                                info={
                                    selectedWorkerGroupId && (
                                        <MarkdownView
                                            markdownStyle={{
                                                textgroup: {
                                                    marginTop: 0,
                                                    marginBottom: 0,
                                                },
                                                paragraph: {
                                                    marginTop: 0,
                                                    marginBottom: 0,
                                                },
                                                heading4: {
                                                    marginTop: 10,
                                                    marginBottom: 0,
                                                },
                                            }}
                                            markdown={getIjmAndLppPlanInfo()}
                                        />
                                    )
                                }
                            />
                            <Gap gap={0.5} />
                            {/* FORCE AVS */}
                            {showForceAvs && (
                                <>
                                    <Gap gap={1} />
                                    <TitleMediumText>
                                        {i18n.t(
                                            "screens.user.create_mission_salary.socialChargesTitle",
                                        )}
                                    </TitleMediumText>
                                    <Gap gap={0.5} />
                                    {renderForceAvs()}
                                    {renderOnDemandLpp()}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {/* CHARGES SOCIALES (force AVS + LPP + IJM) */}
                            <TitleMediumText>
                                {i18n.t("screens.user.create_mission_salary.socialChargesTitle")}
                            </TitleMediumText>
                            <Gap gap={0.5} />
                            {/* FORCE AVS */}
                            {renderForceAvs()}
                            {/* ON-DEMAND LPP CHOICE */}
                            {renderOnDemandLpp()}
                            {/* LPP INFO */}
                            {showLpp &&
                                (contractLppPlan !== undefined ? (
                                    <ButtonField
                                        style={{
                                            marginHorizontal: -gridSize,
                                            paddingHorizontal: gridSize,
                                        }}
                                        asListAction="primary"
                                        disabled={!canChangeLppPlan}
                                        valueColor={canChangeLppPlan ? undefined : colors.primary}
                                        text={
                                            contractLppPlan === null
                                                ? i18n.t("lpp.editor.no_lpp")
                                                : (contractLppPlan?.name as string)
                                        }
                                        right={canChangeLppPlan ? "gear" : undefined}
                                        onPress={canChangeLppPlan ? doEditLppPlan : undefined}
                                        info={
                                            <BodySmallText color={colors.onSurfaceDisabled}>
                                                {i18n.t(
                                                    "screens.user.create_mission_salary.lppLegend",
                                                )}
                                            </BodySmallText>
                                        }
                                    />
                                ) : (
                                    <ButtonField
                                        style={{
                                            marginHorizontal: -gridSize,
                                            paddingHorizontal: gridSize,
                                        }}
                                        asListAction="secondary"
                                        text={i18n.t(
                                            "screens.user.create_mission_salary.setLppAction",
                                        )}
                                        right="gear"
                                        onPress={doEditLppPlan}
                                        info={
                                            <BodySmallText color={colors.onSurfaceDisabled}>
                                                {i18n.t(
                                                    "screens.user.create_mission_salary.lppLegend",
                                                )}
                                            </BodySmallText>
                                        }
                                    />
                                ))}
                            {/* IJM INFO */}
                            {ijmSelection && ijmSelection.ijmGroupId !== undefined ? (
                                <ButtonField
                                    style={{
                                        marginHorizontal: -gridSize,
                                        paddingHorizontal: gridSize,
                                    }}
                                    asListAction="primary"
                                    disabled={!canChangeIjmGroup}
                                    valueColor={canChangeIjmGroup ? undefined : colors.primary}
                                    text={buildIjmDescription(
                                        ijmSelection?.ijmGroupName || undefined,
                                        ijmSelection?.ijmPayFirstDays,
                                        ijmSelection?.ijmPayRemainingPercents,
                                    )}
                                    right={canChangeIjmGroup ? "gear" : undefined}
                                    onPress={canChangeIjmGroup ? doEditIjm : undefined}
                                    info={
                                        <BodySmallText color={colors.onSurfaceDisabled}>
                                            {i18n.t("screens.user.create_mission_salary.ijmLegend")}
                                        </BodySmallText>
                                    }
                                />
                            ) : (
                                <ButtonField
                                    style={{
                                        marginHorizontal: -gridSize,
                                        paddingHorizontal: gridSize,
                                    }}
                                    asListAction="secondary"
                                    text={i18n.t("screens.user.create_mission_salary.setIjmAction")}
                                    right="gear"
                                    onPress={doEditIjm}
                                    info={
                                        <BodySmallText color={colors.onSurfaceDisabled}>
                                            {i18n.t("screens.user.create_mission_salary.ijmLegend")}
                                        </BodySmallText>
                                    }
                                />
                            )}
                        </>
                    )}
                </VStack>

                {loading && <Loading />}
                {!loading && error && <Callout markdown={error} mode="error" />}
                {!loading && !error && salaryDetails && ijmSelection && (
                    <>
                        <Gap gap={1} />
                        <TitleMediumText>
                            {i18n.t("screens.user.create_mission_salary.salary_breakdown")}
                        </TitleMediumText>
                        <Gap gap={0.5} />
                        <SalaryBreakdown
                            earningType={contractEarningType}
                            salaryDetails={salaryDetails}
                        />
                        <Gap gap={1.5} />
                        <TouchableOpacity
                            onPress={() => {
                                navigation.navigate("SalaryCountingDetails", {
                                    earningType: contractEarningType,
                                    payVacationIndemnityWithSalary:
                                        getIsPayVacationIndemnityWithSalary(
                                            mission.currentContract,
                                            mission.legalEmployer?.useLseCct,
                                        ),
                                    useLseCct: mission.legalEmployer?.useLseCct ?? true,
                                    pay13thSalaryWithSalary: getIsPay13thSalaryWithSalary(
                                        mission.currentContract,
                                    ),
                                    salaryDetails: salaryDetails.getSalaryDetails,
                                });
                            }}
                        >
                            <Text style={[fonts.bodyMedium, styles.markdownLink]}>
                                {i18n.t("screens.user.create_mission_salary.show_details")}
                            </Text>
                        </TouchableOpacity>
                    </>
                )}
                <Gap gap={0.5} />
            </VStack>
        </>
    );
};

export default MissionSalary;
